import {IssueRelativeTime} from '@github-ui/ago'
import {MoveDialogTrigger} from '@github-ui/drag-and-drop'
import {
  type RepoIssuesDashboardPinnedIssue,
  RepoIssueState,
  RepoIssueStateReason,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {
  CommentIcon,
  GrabberIcon,
  IssueClosedIcon,
  IssueOpenedIcon,
  KebabHorizontalIcon,
  PinSlashIcon,
  SkipIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import styles from './PinnedIssueCard.module.css'

// A closed issue closed as "not planned" uses the skipped icon; otherwise the
// open/closed issue icons.
const STATE_ICONS = {
  OPEN: {color: 'var(--fgColor-open)', icon: IssueOpenedIcon, description: 'Status: Open.'},
  CLOSED: {color: 'var(--fgColor-done)', icon: IssueClosedIcon, description: 'Status: Closed (completed).'},
  NOT_PLANNED: {color: 'var(--fgColor-muted)', icon: SkipIcon, description: 'Status: Not planned (skipped).'},
} as const

function stateIcon(issue: RepoIssuesDashboardPinnedIssue) {
  if (issue.state === RepoIssueState.CLOSED) {
    return issue.stateReason === RepoIssueStateReason.NOT_PLANNED ? STATE_ICONS.NOT_PLANNED : STATE_ICONS.CLOSED
  }
  return STATE_ICONS.OPEN
}

type PinnedIssueCardProps = {
  issue: RepoIssuesDashboardPinnedIssue
  viewerCanPinIssues: boolean
  onUnpin: (id: string) => void
  getUnpinFocusTarget: (id: string) => HTMLElement | null
  registerOptionsButton: (id: string, button: HTMLButtonElement | null) => void
}

export function PinnedIssueCard({
  issue,
  viewerCanPinIssues,
  onUnpin,
  getUnpinFocusTarget,
  registerOptionsButton,
}: PinnedIssueCardProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const pendingUnpinRef = useRef<{focusTarget: HTMLElement | null} | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleUnpin = () => {
    pendingUnpinRef.current = {focusTarget: getUnpinFocusTarget(issue.id)}
  }

  useEffect(() => {
    const pendingUnpin = pendingUnpinRef.current
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- unpin must wait until ActionMenu unmounts and restores focus to its trigger
    if (isMenuOpen || !pendingUnpin) return

    pendingUnpinRef.current = null
    onUnpin(issue.id)
    pendingUnpin.focusTarget?.focus()
  }, [isMenuOpen, issue.id, onUnpin])

  useEffect(() => {
    registerOptionsButton(issue.id, editButtonRef.current)
    return () => registerOptionsButton(issue.id, null)
  }, [issue.id, registerOptionsButton])

  const state = stateIcon(issue)
  const StateIcon = state.icon
  const commentCount = issue.commentCount ?? 0
  const author = issue.author

  return (
    <div className={styles.PinnedIssueContainer}>
      <div className={styles.contentContainer}>
        <div className={styles.titleOptionsContainer}>
          <Link aria-label={`View ${issue.title}`} className={clsx('css-truncate', styles.Link)} href={issue.url} muted>
            <span style={{'--state-icon-color': state.color}} className={styles.Octicon}>
              <StateIcon aria-label={state.description} />
            </span>
            {issue.titleHtml ? <VerifiedHTMLText html={issue.titleHtml} /> : <span>{issue.title}</span>}
          </Link>
          {viewerCanPinIssues && (
            <ActionMenu anchorRef={editButtonRef} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <ActionMenu.Anchor>
                <IconButton
                  size="small"
                  icon={KebabHorizontalIcon}
                  variant="invisible"
                  aria-label={`Pinned issue #${issue.number} options`}
                  className={styles.IconButton}
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="medium">
                <ActionList>
                  <ActionList.Item onSelect={handleUnpin} aria-label={`Unpin issue #${issue.number}, ${issue.title}`}>
                    <ActionList.LeadingVisual>
                      <PinSlashIcon />
                    </ActionList.LeadingVisual>
                    Unpin
                  </ActionList.Item>
                  <MoveDialogTrigger Component={ActionListItemReorder} returnFocusRef={editButtonRef} />
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </div>
        <div className={styles.metadataContainer}>
          <span className={styles.issueMetadata}>
            #{issue.number} &middot;{' '}
            {author && (
              <Link href={author.resourcePath} {...getHovercardAttributesForType('user', {login: author.login})} muted>
                {author.login}
              </Link>
            )}{' '}
            <span>opened </span>
            <IssueRelativeTime datetime={issue.createdAt} />
          </span>
          {commentCount > 0 && (
            <div className={styles.commentCountContainer}>
              <CommentIcon aria-label={`${commentCount} comments`} className={styles.commentIcon} />
              <span className={styles.commentCountText}>{commentCount}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// `onClick` is supplied by the MoveDialogTrigger component to open the
// keyboard-accessible reorder dialog.
function ActionListItemReorder({onClick}: {onClick?: () => void}) {
  return (
    <ActionList.Item onSelect={onClick}>
      <ActionList.LeadingVisual>
        <GrabberIcon />
      </ActionList.LeadingVisual>
      Advanced move...
    </ActionList.Item>
  )
}
